import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";
import HelpOutlineOutlined from "@mui/icons-material/HelpOutlineOutlined";
import NotificationsActiveOutlined from "@mui/icons-material/NotificationsActiveOutlined";
import NotificationsOffOutlined from "@mui/icons-material/NotificationsOffOutlined";
import { Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import MetaChip, { type MetaChipTone } from "../../../../../components/UIToolkit/Badges/MetaChip";
import { useNursaReadiness } from "../hooks";

type Props = {
	/** No id saved means there is nothing to be connected to — the chips stay away entirely. */
	facilityId: string | null;
	/** The two checks read cross-tenant state belonging to our API client, so they are support-only. */
	isSupport: boolean;
};

type ChipSpec = { labelKey: string; tone: MetaChipTone; icon: ReactNode; testId: string };

/**
 * A tri-state signal rendered as a chip, and the third state is the point.
 *
 * `null` means "we could not ask Nursa", which is NOT "the answer is no". Painting it as a failure
 * during a vendor outage invites an operator to go and "fix" a mapping that was never broken.
 */
const chipFor = (value: boolean | null | undefined, { pass, fail, unknown }: { pass: ChipSpec; fail: ChipSpec; unknown: ChipSpec }): ChipSpec => {
	if (value === true) return pass;
	if (value === false) return fail;
	return unknown;
};

const UNKNOWN_ICON = <HelpOutlineOutlined fontSize="small" />;

const CONNECTION_CHIPS = {
	pass: {
		labelKey: "department.config.integrations.nursa.readiness.connected",
		tone: "success" as MetaChipTone,
		icon: <CheckCircleOutlined fontSize="small" />,
		testId: "dept-config-nursa-readiness-connected",
	},
	fail: {
		labelKey: "department.config.integrations.nursa.readiness.notConnected",
		tone: "error" as MetaChipTone,
		icon: <ErrorOutlineOutlined fontSize="small" />,
		testId: "dept-config-nursa-readiness-not-connected",
	},
	unknown: {
		labelKey: "department.config.integrations.nursa.readiness.unknown",
		tone: "neutral" as MetaChipTone,
		icon: UNKNOWN_ICON,
		testId: "dept-config-nursa-readiness-connection-unknown",
	},
};

const WEBHOOK_CHIPS = {
	pass: {
		labelKey: "department.config.integrations.nursa.readiness.webhookOn",
		tone: "success" as MetaChipTone,
		icon: <NotificationsActiveOutlined fontSize="small" />,
		testId: "dept-config-nursa-readiness-webhook-on",
	},
	fail: {
		// WARNING rather than ERROR, and the distinction is real: a missing webhook is one click away
		// from fixed (the Update webhook button below), while a missing connection needs Nursa.
		labelKey: "department.config.integrations.nursa.readiness.webhookMissing",
		tone: "warning" as MetaChipTone,
		icon: <NotificationsOffOutlined fontSize="small" />,
		testId: "dept-config-nursa-readiness-webhook-missing",
	},
	unknown: {
		labelKey: "department.config.integrations.nursa.readiness.unknown",
		tone: "neutral" as MetaChipTone,
		icon: UNKNOWN_ICON,
		testId: "dept-config-nursa-readiness-webhook-unknown",
	},
};

/**
 * "Is this department actually live on Nursa?" — answered where the question gets asked.
 *
 * A saved Facility ID looks identical whether or not it works. Two things behind it fail silently:
 * Nursa refuses to act on behalf of a facility our API client has no CONNECTION to, and nothing
 * Nursa does reaches us unless the facility is on our shared WEBHOOK registration. Either one
 * leaves a department that reads as configured and exchanges nothing — the shape of the 2026-09-17
 * SW.Montana incident.
 *
 * Sits beside `AgencyIntegrationStatusChip`, which answers a different question: that one is about
 * OUR switch, these are about Nursa's side of the wiring.
 */
export const NursaReadinessChips: FC<Props> = ({ facilityId, isSupport }) => {
	const { t } = useTranslation();
	const hasFacilityId = !!facilityId?.trim();
	const { data } = useNursaReadiness({ enabled: isSupport && hasFacilityId });

	// Nothing to report before an id is saved: the toggle's own chip already says "No Facility ID",
	// and a row of "unknown" chips next to it would read as a fault rather than as a blank slate.
	// Non-support users see no chips at all, matching the picker they also cannot use.
	if (!isSupport || !hasFacilityId) return null;

	const connection = chipFor(data?.connected, CONNECTION_CHIPS);
	const webhook = chipFor(data?.webhookSubscribed, WEBHOOK_CHIPS);

	return (
		<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
			{[connection, webhook].map((chip) => (
				<MetaChip
					key={chip.testId}
					label={t(chip.labelKey)}
					icon={chip.icon}
					tone={chip.tone}
					tooltip={t(`${chip.labelKey}.tooltip`)}
					dataTestId={chip.testId}
				/>
			))}
		</Box>
	);
};
